// src/components/features/UpcomingServices/UrgencyMultiSelector/UrgencyMultiSelector.tsx
//
// Chip-based multi-selector for urgency statuses.
// Follows the same pattern as TravelTypeMultiSelector:
// - Desktop: Chip.Group with horizontal chips (popover in auto mode)
// - Mobile: 2-column grid with touch-friendly buttons
// - Quick actions: Select all / Clear

import React, { useMemo, useCallback } from "react";
import {
  Chip,
  Group,
  Text,
  Stack,
  Box,
  Button,
  Popover,
  Badge,
  UnstyledButton,
  SimpleGrid,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import {
  URGENCY_STATUS_OPTIONS,
  UrgencyStatusConfig,
} from "@/config/urgencyStatuses";

import { UrgencyStatus } from "@/utils/api/ServiceIntervalNextService";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface UrgencyMultiSelectorProps {
  /** Currently selected urgency statuses */
  value?: UrgencyStatus[];

  /** Callback fired when selection changes */
  onChange?: (values: UrgencyStatus[]) => void;

  /** Whether to show as a labeled field */
  showLabel?: boolean;

  /** Custom label text */
  label?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Whether to show select all / clear actions */
  showActions?: boolean;

  /**
   * Display variant:
   * - "auto" (default): Popover on desktop, inline on mobile
   * - "inline": Always show chips inline
   * - "popover": Always use popover
   */
  variant?: "auto" | "inline" | "popover";
}

// ============================================
// Subcomponents
// ============================================

interface UrgencyButtonProps {
  config: UrgencyStatusConfig;
  isSelected: boolean;
  disabled: boolean;
  onToggle: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Mobile-optimized toggle button with proper touch target
 */
const MobileUrgencyButton: React.FC<UrgencyButtonProps> = ({
  config,
  isSelected,
  disabled,
  onToggle,
  t,
}) => {
  return (
    <UnstyledButton
      onClick={onToggle}
      disabled={disabled}
      w="100%"
      py="sm"
      px="md"
      style={(theme) => ({
        minHeight: 48,
        borderRadius: theme.radius.md,
        border: `1px solid ${isSelected
          ? theme.colors[config.color]?.[6] || theme.colors.brand[6]
          : theme.colors.gray[3]
          }`,
        backgroundColor: isSelected
          ? theme.colors[config.color]?.[0] || theme.colors.brand[0]
          : "transparent",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 150ms ease",
      })}
    >
      <Group gap="sm" wrap="nowrap" justify="center">
        <AppIcon
          iconName={config.icon}
          size={18}
          color={
            isSelected
              ? `var(--mantine-color-${config.color}-6)`
              : "var(--mantine-color-gray-6)"
          }
        />
        <Text
          size="sm"
          fw={isSelected ? 600 : 400}
          c={isSelected ? config.color : "dimmed"}
        >
          {t(config.translationKey, { defaultValue: config.value })}
        </Text>
        {isSelected && (
          <AppIcon
            iconName="Check"
            size={16}
            color={`var(--mantine-color-${config.color}-6)`}
          />
        )}
      </Group>
    </UnstyledButton>
  );
};

/**
 * Desktop chip button
 */
const DesktopUrgencyChip: React.FC<UrgencyButtonProps> = ({
  config,
  isSelected,
  disabled,
  onToggle,
  t,
}) => {
  return (
    <Chip
      value={config.value}
      size="md"
      color={config.color}
      variant={isSelected ? "filled" : "outline"}
      disabled={disabled}
      checked={isSelected}
      onChange={onToggle}
      icon={<AppIcon iconName={config.icon} size={14} />}
    >
      {t(config.translationKey, { defaultValue: config.value })}
    </Chip>
  );
};

// ============================================
// Main Component
// ============================================

const UrgencyMultiSelectorComponent: React.FC<UrgencyMultiSelectorProps> = ({
  value = [],
  onChange,
  showLabel = false,
  label,
  disabled = false,
  showActions = true,
  variant = "auto",
}) => {
  const { t } = useTranslation(["services", "common"]);
  const [popoverOpened, { close, toggle }] = useDisclosure(false);
  const { isMobile, isDesktop } = useAppEnvironment();

  const usePopover = useMemo(() => {
    if (variant === "popover") return true;
    if (variant === "inline") return false;
    return isDesktop;
  }, [variant, isDesktop]);

  // Get selected configs for display
  const selectedConfigs = useMemo(() => {
    return URGENCY_STATUS_OPTIONS.filter((opt) => value.includes(opt.value));
  }, [value]);

  // Handle chip toggle
  const handleToggle = useCallback(
    (statusValue: UrgencyStatus) => {
      if (disabled) return;

      const newValue = value.includes(statusValue)
        ? value.filter((v) => v !== statusValue)
        : [...value, statusValue];

      onChange?.(newValue);
    },
    [value, onChange, disabled]
  );

  // Handle select all / clear all
  const handleSelectAll = useCallback(() => {
    if (disabled) return;
    const allValues = URGENCY_STATUS_OPTIONS.map((opt) => opt.value);
    onChange?.(allValues);
  }, [onChange, disabled]);

  const handleClearAll = useCallback(() => {
    if (disabled) return;
    onChange?.([]);
  }, [onChange, disabled]);

  const allSelected = value.length === URGENCY_STATUS_OPTIONS.length;
  const noneSelected = value.length === 0;

  const defaultLabel =
    label ||
    t("services:urgencyFilter.label", { defaultValue: "Urgency" });

  // ============================================
  // Chips list (used in both inline and popover)
  // ============================================

  const chipsList = (
    <Stack gap={isMobile ? "xs" : "sm"}>
      {isMobile ? (
        <SimpleGrid cols={2} spacing="xs" w="100%">
          {URGENCY_STATUS_OPTIONS.map((config) => (
            <MobileUrgencyButton
              key={config.value}
              config={config}
              isSelected={value.includes(config.value)}
              disabled={disabled}
              onToggle={() => handleToggle(config.value)}
              t={t}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Chip.Group multiple value={value}>
          <Group gap="xs" wrap="wrap">
            {URGENCY_STATUS_OPTIONS.map((config) => (
              <DesktopUrgencyChip
                key={config.value}
                config={config}
                isSelected={value.includes(config.value)}
                disabled={disabled}
                onToggle={() => handleToggle(config.value)}
                t={t}
              />
            ))}
          </Group>
        </Chip.Group>
      )}

      {/* Quick actions */}
      {showActions && (
        <Group gap="sm" justify={isMobile ? "center" : "flex-start"}>
          {!allSelected && (
            <Text
              size="xs"
              c="brand"
              style={{ cursor: disabled ? "default" : "pointer" }}
              onClick={handleSelectAll}
            >
              {t("common:buttons.selectAll", { defaultValue: "Select all" })}
            </Text>
          )}
          {!noneSelected && (
            <Text
              size="xs"
              c="dimmed"
              style={{ cursor: disabled ? "default" : "pointer" }}
              onClick={handleClearAll}
            >
              {t("common:buttons.clear", { defaultValue: "Clear" })}
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );

  // ============================================
  // Render: Popover mode (desktop)
  // ============================================

  if (usePopover) {
    return (
      <Box>
        {showLabel && (
          <Text size="sm" fw={500} mb={6} c="dimmed">
            {defaultLabel}
          </Text>
        )}

        <Popover
          opened={popoverOpened}
          onClose={close}
          position="bottom-start"
          width={320}
          shadow="md"
          withArrow
        >
          <Popover.Target>
            <Button
              variant="default"
              size="md"
              onClick={toggle}
              disabled={disabled}
              rightSection={
                <AppIcon
                  iconName={popoverOpened ? "ChevronUp" : "ChevronDown"}
                  size={14}
                />
              }
              styles={{
                root: { fontWeight: 400 },
                section: { marginLeft: 4 },
              }}
            >
              {noneSelected ? (
                <Text size="sm" c="dimmed">
                  {t("services:urgencyFilter.all", {
                    defaultValue: "All statuses",
                  })}
                </Text>
              ) : allSelected ? (
                <Text size="sm">
                  {t("services:urgencyFilter.allStatuses", {
                    defaultValue: "All statuses",
                  })}
                </Text>
              ) : (
                <Group gap={6} wrap="nowrap">
                  <Badge
                    size="md"
                    color={selectedConfigs[0].color}
                    variant="light"
                    radius="sm"
                    leftSection={
                      <AppIcon iconName={selectedConfigs[0].icon} size={12} />
                    }
                  >
                    {t(selectedConfigs[0].translationKey, {
                      defaultValue: selectedConfigs[0].value,
                    })}
                  </Badge>
                  {selectedConfigs.length > 1 && (
                    <Badge size="md" variant="light" color="gray" radius="sm">
                      +{selectedConfigs.length - 1}
                    </Badge>
                  )}
                </Group>
              )}
            </Button>
          </Popover.Target>

          <Popover.Dropdown p="md">{chipsList}</Popover.Dropdown>
        </Popover>
      </Box>
    );
  }

  // ============================================
  // Render: Inline mode (mobile/tablet)
  // ============================================

  return (
    <Box>
      {showLabel && (
        <Text size="sm" fw={500} mb={6} c="dimmed">
          {defaultLabel}
        </Text>
      )}
      {chipsList}
    </Box>
  );
};

export const UrgencyMultiSelector = React.memo(UrgencyMultiSelectorComponent);